import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Seo from '../components/common/Seo';
import CategoryNav from '../components/common/CategoryNav';
import BlogList from '../components/blogs/BlogList';
import NotFound from './NotFound';
import { categoryFromSlug, isKnownCategorySlug } from '../utils/slug';
import { categoryDescription } from '../constants/blogCategories';
import { breadcrumbSchema, collectionSchema } from '../utils/jsonLd';

function CategoryPage() {
  const { categorySlug } = useParams();
  const [posts, setPosts] = useState([]);
  const onLoaded = useCallback((list) => setPosts(list), []);

  // An unknown category must 404 rather than render an empty list — otherwise
  // Google indexes an endless supply of thin pages for typo'd URLs.
  if (!isKnownCategorySlug(categorySlug)) return <NotFound />;

  const category = categoryFromSlug(categorySlug);
  const description = categoryDescription(category);
  const path = `/category/${categorySlug}`;

  return (
    <div className="container page">
      <Seo
        title={`${category} Articles`}
        path={path}
        description={description}
        jsonLd={[
          collectionSchema({ name: `${category} Articles`, description, path, posts }),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Articles', path: '/blogs' },
            { name: category, path },
          ]),
        ]}
      />

      <nav className="breadcrumb" aria-label="Breadcrumb">
        <ol>
          <li>
            <Link to="/">Home</Link>
          </li>
          <li>
            <Link to="/blogs">Articles</Link>
          </li>
          <li aria-current="page">{category}</li>
        </ol>
      </nav>

      <header className="page-head">
        <h1 className="page-title">{category}</h1>
        <p className="page-sub">{description}</p>
      </header>

      <CategoryNav ariaLabel="Switch category" />

      <BlogList
        category={category}
        onLoaded={onLoaded}
        emptyMessage={`No ${category} articles published yet.`}
      />
    </div>
  );
}

export default CategoryPage;
